import Monitor01 from "@untitled-ui/icons-react/build/esm/Monitor01";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ErrorImage from "../../../../assets/images/common/no-image-placeholder-square.png";
import FrontSystemsLogo from "../../../../assets/images/FrontSystems_logo_RGB_WHITE_1.png";
import FrontSystemsLogoGray from "../../../../assets/images/FrontSystems_logo_RGB_WHITE_GRAY.png";
import { formatPrice } from "../../../../utils/Formatter";
import * as Library from "../../../../utils/Library";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import { findCategoryMapping, isMissingSourceValue, sourceValuesMatch } from "./sourceMappingIdentity";

// #region CONSTANTS
const PENDING_RULES_TEXT = "Pending rules";
const hasMappingValue = value => value !== undefined && value !== null && value !== "";
// #endregion

// #region SCOPED CSS
const FormStyles = styled.div`
    .pos-header {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 0 10px;
        margin-bottom: 8px;
    }

    .pos-header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }

    .pos-main-title {
        color: #fff;
        font-size: 20px;
        flex: 1;
    }

    .pos-subtitle-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .pos-subtitle {
        color: var(--text-icons-gray-100);
        font-size: 14px;
    }

    .pos-subtitle.orange {
        color: var(--text-icons-orange-500) !important;
    }

    .pos-random-text {
        color: var(--text-icons-orange-500);
        font-size: 12px;
        text-align: right;
        padding-right: 8px;
    }

    .product-details-card {
        background: var(--backgrounds-lines-n-300);
        border-radius: 5px;
        padding: 16px;
    }

    .product-details-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 22px;
    }

    .product-details-header-icon {
        color: var(--text-icons-green-500);
        font-size: 14px;
    }

    .product-details-header-title {
        color: var(--text-icons-green-500);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .product-content {
        display: flex;
        gap: 16px;
    }

    /* LEFT COLUMN: Image + Description */
    .left-column {
        width: 225px;
        min-width: 225px;
        display: flex;
        flex-direction: column;
        gap: 29px;
    }

    .product-image {
        width: 225px;
        height: 201px;
        border-radius: 4px;
        object-fit: cover;
        background: var(--backgrounds-lines-n-400);
    }

    .description-box {
        background: var(--backgrounds-lines-n-600);
        border-radius: 4px;
        padding: 12px;
        color: var(--text-icons-gray-100);
        font-size: 12px;
        line-height: 1.5;
        height: 393px;
        overflow-y: auto;
        width: 100%;
    }

    .description-box svg {
        color: currentColor;
        fill: currentColor;
    }

    .description-box svg [stroke]:not([stroke="none"]) {
        stroke: currentColor !important;
    }

    .description-box svg [fill]:not([fill="none"]) {
        fill: currentColor !important;
    }

    /* CENTER COLUMN: Identification, Cost, Pricing */
    .center-column {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0;
        width: 250px;
        height: 624px;
    }

    .table-section-header {
        display: flex;
        align-items: center;
        gap: 4px;
        padding-bottom: 6px;
        border-bottom: 1px solid var(--backgrounds-lines-n-100);
        margin-bottom: 12px;
    }

    .table-section-title {
        color: var(--text-icons-gray-100);
        font-size: 12px;
        font-weight: 500;
    }

    .table-section-title.muted {
        color: var(--text-icons-gray-300);
    }

    .info-row {
        display: flex;
        gap: 16px;
        margin-bottom: 17px;
    }

    .info-field {
        flex: 1;
    }

    .info-field-label {
        color: var(--text-icons-gray-200);
        font-size: 10px;
        margin-bottom: 5px;
    }

    .info-field-label.muted {
        color: var(--text-icons-gray-300);
    }

    .info-field-value {
        color: var(--text-icons-green-500);
        font-size: 12px;
        white-space: pre-wrap;
    }

    .info-field-value.muted {
        color: var(--text-icons-gray-300);
    }

    .info-field-value.bold {
        font-weight: 600;
    }

    .info-field-value.text-orange {
        color: var(--text-icons-orange-500) !important;
    }

    .cost-row {
        height: 79px !important;
    }

    /* RIGHT COLUMN: Tags, Collections, Identifiers */
    .right-column {
        width: 218px;
        min-width: 218px;
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .right-section {
        margin-bottom: 44px;
    }

    .right-section-value {
        color: var(--text-icons-gray-200);
        font-size: 10px;
        padding-top: 8px;
    }

    /* Identifiers tabs */
    .identifier-tabs {
        display: flex;
        gap: 16px;
        margin-bottom: 0px;
    }

    .identifier-tab {
        cursor: pointer;
        padding-bottom: 4px;
        font-size: 12px;
        color: var(--text-icons-gray-200);
        border-bottom: 2px solid transparent;
    }

    .identifier-tab.active {
        color: var(--text-icons-green-500);
        border-bottom: 2px solid var(--text-icons-green-500);
    }

    .identifier-tab.muted {
        color: var(--text-icons-gray-300);
        border-bottom: 2px solid var(--text-icons-gray-300);
    }

    .identifier-list {
        max-height: 238px;
        overflow-y: auto;
    }

    .identifier-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5.5px 8px;
        font-size: 12px;
    }

    .identifier-code {
        color: var(--text-icons-gray-100);
    }

    .identifier-size {
        color: var(--text-icons-gray-100);
        text-align: right;
    }

    .dropdown-wrapper {
        width: 273px;
    }

    .form-field-no-margin .FormComponentContainer {
        min-height: auto;
        margin-bottom: 0 !important;
    }
    .form-field-no-margin .FormComponentContainer .Title {
        display: none;
    }

    .POS-Preview-Form .BO-card-component {
        padding-top: 14px !important;
    }

    .POS-Preview-Form .BO-card-header {
        height: 19px !important;
    }

    //MUTED SECTION
    .form-muted .card-title,
    .form-muted .pos-main-title,
    .form-muted .pos-subtitle-row,
    .form-muted .pos-subtitle {
        color: var(--text-icons-gray-300) !important;
    }

    .form-muted .product-details-card span {
        color: var(--text-icons-gray-300) !important;
    }

    .form-muted .product-details-card svg {
        color: var(--text-icons-gray-300) !important;
    }
`;
// #endregion

export default function POSExpPreviewCard({
    previewData = {},
    isProductRandom = false,
    brandExportRules = {},
    identifiers = {},
    allBrandItems,
    hasLabelBrandProducts = true,
    onProductChange,
    formsEnabled = false,
    descriptionsIncluded,
    seasonMappings = [],
    genderMappings = [],
    colorMappings = [],
    categoryFieldName,
    categoryMappings = [],
    relatedSizeGroupMappings = [],
    mappingCompletion = {}
}) {
    // #region STATES
    const [selectedTab, setSelectedTab] = useState("gtins");
    const [isLoading, setIsLoading] = useState(false);
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const lastTranslatedSourceRef = useRef("");
    const lastTranslatedLangRef = useRef("");
    // #endregion

    // #region DATA MODEL STATES
    const hasPreviewProducts = hasLabelBrandProducts && !!allBrandItems?.length;

    const computedData = useMemo(() => {
        let nameChanged = false;
        const hasProductName = previewData.name !== undefined && previewData.name !== null && previewData.name !== "";
        let computedName = hasProductName ? previewData.name : "-";

        //append product name suffix directly after product name (no added space — user adds their own)
        if (hasProductName && brandExportRules.addProductNameSuffix && brandExportRules.productNameSuffix)
            computedName = `${computedName}${brandExportRules.productNameSuffix}`;

        if (hasProductName && brandExportRules.addColorName) {
            const colorNameSource = brandExportRules.productNameColorSource === "POS" ? "POS" : "Brand";

            if (colorNameSource === "POS") {
                const mappedColor = colorMappings.find(mapping => mapping?.labelBrandName === previewData.color);
                computedName = `${computedName} - ${mappedColor?.exportName || ""}`;
            } else {
                computedName = `${computedName} - ${previewData.color}`;
            }
        }

        //apply format to item name. Formats: Capitalized, Uppercase
        //format is applied last so it covers everything (name + suffix + color)
        if (hasProductName && brandExportRules.format === "Capitalized") {
            const currentItemName = computedName;

            // Convert to Title Case (capitalize first letter of each word, rest lowercase)
            const newItemName = currentItemName.replace(/\b\w+/g, word => {
                // Preserve Roman numerals (I, V, X, L, C, D, M combinations)
                if (/^[ivxlcdm]+$/i.test(word)) return word.toUpperCase();
                // Regular title case
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            });

            computedName = newItemName;
        } else if (hasProductName && brandExportRules.format === "Uppercase") computedName = computedName.toUpperCase();

        // Bold only if user-set rules actively change the name (Uppercase, addColorName, or suffix)
        // Default "Capitalized" format is not considered a user change
        nameChanged =
            hasProductName &&
            (brandExportRules.format === "Uppercase" ||
                brandExportRules.addColorName ||
                (brandExportRules.addProductNameSuffix && !!brandExportRules.productNameSuffix));

        // Compute description: base description + included fields with separator and field name
        const descriptionFields = {
            includeCategory: { label: "Category", value: previewData.category },
            includeSpecifications: { label: "Specifications", value: previewData.specifications },
            includeComposition: { label: "Composition", value: previewData.composition },
            includeWashAndCare: { label: "Wash & Care", value: previewData.washCare },
            includeFeatures: { label: "Features", value: previewData.features },
            includeQualityDescription: { label: "Quality description", value: previewData.qualityDescription },
            includeCountryOfOrigin: { label: "Country of origin", value: previewData.countryOfOrigin }
        };

        let computedDescription = previewData.description || "";
        Object.entries(descriptionFields).forEach(([key, { label, value }]) => {
            if (!descriptionsIncluded?.[key] || !value) return;
            computedDescription += `<p>______________________</p><br><strong>${label}</strong><p>${value}</p>`;
        });

        // Compute prices: apply rounding if enabled
        let computedCostCurrent = previewData.costCurrent;
        let computedSalePrice = previewData.salePrice;
        let priceChanged = false;
        let costChanged = false;
        let hasTariffApplied = false;

        const cleanCost = String(computedCostCurrent)
            .replace(/\s/g, "")
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".");
        let priceIN = Number(cleanCost);

        if (Number.isFinite(priceIN)) {
            const originalPrice = priceIN;

            // 1) costDiscountPercentage
            if (brandExportRules.applyDiscount && brandExportRules.costDiscountPercentage) {
                const discountStr = brandExportRules.costDiscountPercentage.replace("%", "").replace(",", ".");
                const costDiscountPercentageNum = Number(discountStr);

                if (!isNaN(costDiscountPercentageNum)) {
                    priceIN = priceIN * (1 - costDiscountPercentageNum / 100);
                    priceIN = Math.round(priceIN * 1000) / 1000; // round to 3 decimals
                }
            }

            // 2) costTariffPercentage
            if (brandExportRules.applyCostTariff && brandExportRules.costTariffPercentage) {
                const tariffStr = brandExportRules.costTariffPercentage.replace("%", "").replace(",", ".");
                const costTariffPercentageNum = Number(tariffStr);

                if (!isNaN(costTariffPercentageNum) && costTariffPercentageNum > 0) {
                    priceIN = priceIN * (1 + costTariffPercentageNum / 100);
                    priceIN = Math.round(priceIN * 1000) / 1000; // round to 3 decimals
                    hasTariffApplied = true;
                }
            }

            if (priceIN !== originalPrice) {
                computedCostCurrent = priceIN;
                costChanged = true;
            }
        }

        if (brandExportRules.roundPrice && brandExportRules.roundingKey) {
            const { priceOUT } = getPrices(
                previewData.costCurrent,
                previewData.salePrice,
                brandExportRules.roundingKey
            );
            computedSalePrice = priceOUT;
            if (computedSalePrice !== parsePriceToNumber(previewData.salePrice)) {
                priceChanged = true;
            }
        }

        // Compute season: apply mapping if available
        let computedSeason = previewData.season || "-";
        let seasonChanged = false;
        if (seasonMappings?.length && previewData.season) {
            const mapped = seasonMappings.find(m => m.labelBrandName === previewData.season);
            if (mapped?.exportName !== null && mapped?.exportName !== undefined) {
                computedSeason = mapped.exportName;
                seasonChanged = true;
            }
        }

        // Compute gender: apply mapping if available
        let computedGender = isMissingSourceValue(previewData.gender) ? "-" : previewData.gender;
        let genderChanged = false;
        const mappedGender = genderMappings.find(m => sourceValuesMatch(m.originalGenderName, previewData.gender));
        if (mappedGender?.userGenderName !== undefined && mappedGender?.userGenderName !== null) {
            computedGender =
                mappedGender.userGenderName === "Leave empty / Unknown" || mappedGender.userGenderName === ""
                    ? "Empty / Unknown"
                    : mappedGender.userGenderName;
            genderChanged = true;
        }

        // Resolve category-based rules through the selected source field and the product's original gender
        let categorySourceValue = null;
        if (categoryFieldName === "Category") categorySourceValue = previewData.category;
        else if (categoryFieldName === "Product type") categorySourceValue = previewData.productGroup;
        const mappedCategory = categoryFieldName
            ? findCategoryMapping(categoryMappings, categorySourceValue, previewData.gender, previewData.sizes)
            : null;

        // Compute product group: apply the saved POS category mapping
        let computedProductGroup = previewData.productGroup || "-";
        let productGroupChanged = false;
        if (mappedCategory?.exportName !== null && mappedCategory?.exportName !== undefined) {
            computedProductGroup =
                mappedCategory.completeCategoryNameFromPOS ||
                [mappedCategory.exportName, mappedCategory.subCategoryExportName].filter(hasMappingValue).join(" - ");
            productGroupChanged = true;
        }

        // Compute Size System from the Label Brand category/product-type conversion table
        const mappedSizeSystem = categoryFieldName
            ? findCategoryMapping(relatedSizeGroupMappings, categorySourceValue, previewData.gender, previewData.sizes)
            : null;
        const hasMappedSizeSystem = hasMappingValue(mappedSizeSystem?.exportName);
        const computedSizeSystem = hasMappedSizeSystem ? mappedSizeSystem.exportName : "-";

        // Compute color: apply mapping if available
        let computedColor = previewData.color || "-";
        let colorChanged = false;
        if (colorMappings?.length && previewData.color) {
            const mapped = colorMappings.find(m => m.labelBrandName === previewData.color);
            if (mapped?.exportName !== null && mapped?.exportName !== undefined) {
                computedColor = mapped.exportName;
                colorChanged = true;
            }
        }

        // Compute color code: use the original Label Brand color name when the COLORS rule is active
        let computedColorCode = previewData.colorCode || "-";
        const colorCodeChanged = !!brandExportRules.useBrandColorNameInColorCode;
        if (brandExportRules.useBrandColorNameInColorCode) computedColorCode = previewData.color;

        const showPendingRules = formsEnabled && hasPreviewProducts;
        const genderPending = showPendingRules && !mappedGender && !mappingCompletion.genders;
        const seasonPending = showPendingRules && !mappingCompletion.seasons;
        const productGroupPending = showPendingRules && !hasMappingValue(mappedCategory?.exportName);
        const sizeSystemPending = showPendingRules && !hasMappedSizeSystem;
        const colorPending = showPendingRules && !mappingCompletion.colors;
        const colorCodePending = colorPending;
        let displayedSizeSystem = "-";
        if (sizeSystemPending) displayedSizeSystem = PENDING_RULES_TEXT;
        else if (showPendingRules) displayedSizeSystem = computedSizeSystem;

        return {
            ...previewData,
            name: computedName,
            nameChanged: nameChanged,
            color: colorPending ? PENDING_RULES_TEXT : computedColor,
            colorChanged: colorChanged,
            colorPending,
            colorCode: colorCodePending ? PENDING_RULES_TEXT : computedColorCode,
            colorCodeChanged: colorCodeChanged,
            colorCodePending,
            description: computedDescription,
            costCurrent: computedCostCurrent,
            costChanged: costChanged,
            hasTariffApplied: hasTariffApplied,
            salePrice: computedSalePrice,
            priceChanged: priceChanged,
            season: seasonPending ? PENDING_RULES_TEXT : computedSeason,
            seasonChanged: seasonChanged,
            seasonPending,
            gender: genderPending ? PENDING_RULES_TEXT : computedGender,
            genderChanged: genderChanged,
            genderPending,
            productGroup: productGroupPending ? PENDING_RULES_TEXT : computedProductGroup,
            productGroupChanged,
            productGroupPending,
            sizeSystem: displayedSizeSystem,
            sizeSystemChanged: hasMappedSizeSystem,
            sizeSystemPending
        };
    }, [
        previewData,
        brandExportRules,
        descriptionsIncluded,
        seasonMappings,
        genderMappings,
        colorMappings,
        categoryFieldName,
        categoryMappings,
        relatedSizeGroupMappings,
        mappingCompletion,
        formsEnabled,
        hasPreviewProducts
    ]); //useMemo to get computed preview data based on the rules
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        // If translation is disabled or no language selected, clear and return
        if (!brandExportRules.translateDesc || !brandExportRules.translateDescLangCode) {
            setTranslatedDescription(null);
            lastTranslatedSourceRef.current = "";
            lastTranslatedLangRef.current = "";
            return;
        }

        const sourceDesc = computedData.description;
        const language = brandExportRules.translateDescLanguage;

        // Skip if same source and language were already translated
        if (sourceDesc === lastTranslatedSourceRef.current && language === lastTranslatedLangRef.current) return;

        let cancelled = false;

        const translate = async () => {
            const result = await generateTranslationRequest(sourceDesc, language);
            if (cancelled) return;
            if (result) {
                setTranslatedDescription(result);
                lastTranslatedSourceRef.current = sourceDesc;
                lastTranslatedLangRef.current = language;
            }
        };

        translate();

        return () => {
            cancelled = true;
        };
    }, [computedData.description, brandExportRules.translateDesc, brandExportRules.translateDescLangCode]);
    // #endregion

    // #region METHODS FOR CONVERTIONS
    function parsePriceToNumber(str) {
        const clean = String(str)
            .replace(/\s/g, "")
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".");

        const n = Number(clean);
        return Number.isFinite(n) ? n : undefined;
    }

    function calculateNextIncrement(price, increment) {
        // increment = -50, -5, -1, 1, 0.5, 5, 10, 50, 100
        const cents = Math.round(price * 100); // avoid 1.9999 => it's 1.99
        if (increment === -1) {
            // Edge case rule. Example: from 2000 to 1999
            const isCleanPrice = cents % 1000 === 0;
            if (isCleanPrice) return Math.max(0, cents - 100) / 100;
            else return cents / 100;
        } else if (increment > 0) {
            const incrementCents = Math.round(increment * 100);
            const roundedCents = Math.ceil(cents / incrementCents) * incrementCents;
            return roundedCents / 100;
        } else if (increment < 0) {
            const incrementCents = Math.round(-increment * 100);
            const roundedCents = Math.floor(cents / incrementCents) * incrementCents;
            return roundedCents / 100;
        } else {
            return cents / 100; // if (incremente === 0)
        }
    }

    function getPrices(costCurrent, salePrice, pricesRoundUp) {
        // Prices based on exportProductsConfigurations
        const priceIN = parsePriceToNumber(costCurrent); // cost from supplier
        let priceOUT = parsePriceToNumber(salePrice); // retail/sale price

        if (priceOUT) {
            if (pricesRoundUp) {
                const increment =
                    pricesRoundUp === "previous-50"
                        ? -50
                        : pricesRoundUp === "previous-5"
                          ? -5
                          : pricesRoundUp === "previous-1"
                            ? -1
                            : pricesRoundUp === "nextWhole" //TODO: UNAVAILABLE for now
                              ? 1
                              : pricesRoundUp === "next0,50"
                                ? 0.5
                                : pricesRoundUp === "next5"
                                  ? 5
                                  : pricesRoundUp === "next10"
                                    ? 10
                                    : pricesRoundUp === "next50"
                                      ? 50
                                      : pricesRoundUp === "next100"
                                        ? 100
                                        : 0;

                if (increment > 0 || increment < 0) priceOUT = calculateNextIncrement(priceOUT, increment);
                else throw new Error(`Invalid config for pricesRoundUp: ${pricesRoundUp}.`);
            }
        } else priceOUT = undefined;

        return { priceOUT, priceIN };
    }

    const generateTranslationRequest = async (textToTranslate, language) => {
        if (!textToTranslate || !language) return textToTranslate;

        const request = { textToTranslate: textToTranslate, languageName: language };

        const res = await Library.makeApiRequest({
            endpoint: "Global/generateTranslation",
            method: "POST",
            data: request,
            setLoader: setIsLoading
        });

        if (res?.data?.status !== 200) return textToTranslate;

        const translation = res?.data?.data?.textTranslation;
        return translation;
    };
    // #endregion

    // #region RENDER
    return (
        <FormStyles className="w-100">
            <FormContainer
                className={`POS-Preview-Form w-100 ${formsEnabled ? "" : "form-muted"}`}
                title={
                    <img
                        src={formsEnabled ? FrontSystemsLogo : FrontSystemsLogoGray}
                        alt="Front Systems Logo"
                        height={"24px"}
                    />
                }>
                <div>
                    <div className="pos-header">
                        <div className="pos-header-row">
                            <div className="pos-main-title">This is how you'll get it in Front Systems</div>
                            <div className="dropdown-wrapper form-field-no-margin">
                                <FormField
                                    type="dropdown"
                                    varName="itemSelect"
                                    dropdownData={allBrandItems}
                                    isDropdownSearchable={true}
                                    isDropdownMultiselect={false}
                                    placeholder="Select one item example"
                                    onDropdownChange={data => onProductChange?.(data?.value)}
                                    formFieldColumSize={1}
                                    styling={{ formFieldHeight: "27px", dropdownOverlayTop: "35px" }}
                                    requiredType={formsEnabled && hasPreviewProducts ? "simple" : "locked"}
                                    disableTooltip={true}
                                    disableAssistiveText={true}
                                />
                            </div>
                        </div>
                        <div className="pos-subtitle-row">
                            <span className={`pos-subtitle ${!hasPreviewProducts ? "orange" : ""}`}>
                                {hasPreviewProducts
                                    ? `All items from ${previewData?.brand || "the brand"} will look like this in your POS`
                                    : "Front Systems preview will appear once the first product is available."}
                            </span>
                            {!hasPreviewProducts ? (
                                <span className="pos-random-text">No products yet - no preview available</span>
                            ) : formsEnabled && isProductRandom ? (
                                <span className="pos-random-text">Random item selected for preview</span>
                            ) : null}
                        </div>
                    </div>

                    <div className="product-details-card">
                        <div className="product-details-header">
                            <span className="product-details-header-icon">
                                <Monitor01 height="16px" />
                            </span>
                            <span className="product-details-header-title">PRODUCT DETAILS</span>
                        </div>

                        <div className="product-content">
                            {/* LEFT COLUMN */}
                            <div className="left-column">
                                <img
                                    className="product-image"
                                    src={previewData.image || ErrorImage}
                                    alt="Product"
                                    onError={e => {
                                        e.target.src = ErrorImage;
                                    }}
                                />
                                <ContainerLoader isLoading={isLoading} isLoadingOverlay={true} addBlurFilters={false}>
                                    <div
                                        className="description-box"
                                        dangerouslySetInnerHTML={{
                                            __html: computedData.description
                                                ? (translatedDescription ?? computedData.description)
                                                : ""
                                        }}></div>
                                </ContainerLoader>
                            </div>

                            {/* CENTER COLUMN */}
                            <div className="center-column">
                                {/* Identification */}
                                <div className="table-section-header">
                                    <span className={`table-section-title ${formsEnabled ? "" : "muted"}`}>
                                        Identification
                                    </span>
                                </div>

                                <InfoRow
                                    label="Name *"
                                    value={computedData.name}
                                    isBold={computedData.nameChanged}
                                    enabled={formsEnabled}
                                />
                                <InfoRowDouble
                                    label1="Product number"
                                    value1={previewData.productNumber}
                                    label2="Color code"
                                    value2={computedData.colorCode}
                                    value2Style={{
                                        fontWeight:
                                            computedData.colorCodeChanged && !computedData.colorCodePending
                                                ? "600"
                                                : "400"
                                    }}
                                    value2Warning={computedData.colorCodePending}
                                    enabled={formsEnabled}
                                />
                                <InfoRowDouble
                                    label1="Brand *"
                                    value1={previewData.brand}
                                    label2="Product group *"
                                    value2={computedData.productGroup}
                                    value2Style={{
                                        fontWeight:
                                            computedData.productGroupChanged && !computedData.productGroupPending
                                                ? "600"
                                                : "400"
                                    }}
                                    value2Warning={computedData.productGroupPending}
                                    enabled={formsEnabled}
                                />
                                <InfoRowDouble
                                    label1="Gender"
                                    value1={computedData.gender}
                                    value1Style={{
                                        fontWeight:
                                            computedData.genderChanged && !computedData.genderPending ? "600" : "400"
                                    }}
                                    value1Warning={computedData.genderPending}
                                    label2="Season"
                                    value2Style={{
                                        fontWeight:
                                            computedData.seasonChanged && !computedData.seasonPending ? "600" : "400"
                                    }}
                                    value2={computedData.season}
                                    value2Warning={computedData.seasonPending}
                                    enabled={formsEnabled}
                                />
                                <InfoRowDouble
                                    label1="Color"
                                    value1={computedData.color}
                                    value1Style={{
                                        fontWeight:
                                            computedData.colorChanged && !computedData.colorPending ? "600" : "400"
                                    }}
                                    value1Warning={computedData.colorPending}
                                    label2="Size system"
                                    value2={computedData.sizeSystem}
                                    value2Style={{
                                        fontWeight:
                                            computedData.sizeSystemChanged && !computedData.sizeSystemPending
                                                ? "600"
                                                : "400"
                                    }}
                                    value2Warning={computedData.sizeSystemPending}
                                    enabled={formsEnabled}
                                />

                                {/* Cost */}
                                <div className="table-section-header" style={{ marginTop: "16px" }}>
                                    <span className={`table-section-title ${!formsEnabled ? "muted" : ""}`}>Cost</span>
                                </div>
                                {!hasPreviewProducts ? (
                                    <InfoRow
                                        customClass="cost-row"
                                        label="Cost (current)"
                                        value="-"
                                        enabled={formsEnabled}
                                    />
                                ) : formsEnabled && ["", "-", undefined, null].includes(computedData.costCurrent) ? (
                                    <InfoRowOrange
                                        customClass="cost-row"
                                        label="Cost (current)"
                                        value={"Not provided by the brand"}
                                    />
                                ) : computedData.hasTariffApplied ? (
                                    <InfoRowOrange
                                        customClass="cost-row"
                                        label="Cost (current)"
                                        value={formatPrice(computedData.costCurrent, "", "-", true, 2, "start")}
                                    />
                                ) : (
                                    <InfoRow
                                        customClass="cost-row"
                                        label="Cost (current)"
                                        value={formatPrice(computedData.costCurrent, "", "-", true, 2, "start")}
                                        enabled={formsEnabled}
                                        isBold={computedData.costChanged}
                                    />
                                )}

                                {/* Pricing */}
                                <div className="table-section-header" style={{ marginTop: "16px" }}>
                                    <span className={`table-section-title ${!formsEnabled ? "muted" : ""}`}>
                                        Pricing
                                    </span>
                                </div>
                                <InfoRow
                                    label="Sale price"
                                    value={formatPrice(computedData.salePrice, "", "-", true, 2)}
                                    enabled={formsEnabled}
                                    isBold={computedData.priceChanged}
                                />
                            </div>

                            {/* RIGHT COLUMN */}
                            <div className="right-column">
                                {/* Tags */}
                                <div className="right-section">
                                    <div className="table-section-header">
                                        <span className={`table-section-title ${!formsEnabled ? "muted" : ""}`}>
                                            Tags
                                        </span>
                                    </div>
                                    <div className="right-section-value">{"-"}</div>
                                </div>

                                {/* Collections */}
                                <div className="right-section">
                                    <div className="table-section-header">
                                        <span className={`table-section-title ${!formsEnabled ? "muted" : ""}`}>
                                            Collections
                                        </span>
                                    </div>
                                    <div className="right-section-value">{"-"}</div>
                                </div>

                                {/* Identifiers */}
                                <div>
                                    <div className="table-section-header">
                                        <span className={`table-section-title ${!formsEnabled ? "muted" : ""}`}>
                                            Identifiers
                                        </span>
                                    </div>
                                    <div className="identifier-tabs">
                                        <span
                                            className={`identifier-tab ${selectedTab === "gtins" ? "active" : ""} ${!formsEnabled ? "muted" : ""}`}
                                            onClick={() => setSelectedTab("gtins")}>
                                            GTINs
                                        </span>
                                        <span
                                            className={`identifier-tab ${selectedTab === "skus" ? "active" : ""} ${!formsEnabled ? "muted" : ""}`}
                                            onClick={() => setSelectedTab("skus")}>
                                            SKUs
                                        </span>
                                    </div>
                                    <div className="identifier-list">
                                        {["gtins", "skus"].includes(selectedTab) ? (
                                            identifiers[selectedTab]?.map((item, index) => (
                                                <div key={index} className="identifier-row">
                                                    <span className="identifier-code">{item.code}</span>
                                                    <span className="identifier-size">{item.size}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="identifier-row">
                                                <span className="identifier-code">-</span>
                                                <span className="identifier-size"></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </FormContainer>
        </FormStyles>
    );
    // #endregion
}

// #region SMALL COMPONENTS
function InfoRow({ label, value, isBold = false, enabled = true, customClass }) {
    const isMuted = !label || label === "-" || !enabled;

    return (
        <div className={`info-row ${customClass}`}>
            <div className="info-field">
                <div className={`info-field-label ${!enabled ? "muted" : ""}`}>{label}</div>
                <div className={`info-field-value ${isBold ? "bold" : ""} ${isMuted ? "muted" : ""}`}>
                    {value ?? "-"}
                </div>
            </div>
        </div>
    );
}

function InfoRowOrange({ label, value, customClass }) {
    return (
        <div className={`info-row ${customClass}`}>
            <div className="info-field">
                <div className={"info-field-label"}>{label}</div>
                <div className={"info-field-value text-orange"}>{value ?? "-"}</div>
            </div>
        </div>
    );
}

function InfoRowDouble({
    label1,
    value1,
    label2,
    value2,
    enabled = true,
    label1Muted = false,
    label2Muted = false,
    value1Warning = false,
    value2Warning = false,
    value1Style = {},
    value2Style = {}
}) {
    const isLabel1Muted = !label1 || label1 === "-" || !enabled || label1Muted;
    const isLabel2Muted = !label2 || label2 === "-" || !enabled || label2Muted;

    return (
        <div className="info-row">
            <div className="info-field">
                <div className={`info-field-label ${!enabled ? "muted" : ""}`}>{label1}</div>
                <div
                    className={`info-field-value ${isLabel1Muted ? "muted" : ""} ${value1Warning ? "text-orange" : ""}`}
                    style={value1Style}>
                    {value1 || "-"}
                </div>
            </div>
            <div className="info-field">
                <div className={`info-field-label ${!enabled ? "muted" : ""}`}>{label2}</div>
                <div
                    className={`info-field-value ${isLabel2Muted ? "muted" : ""} ${value2Warning ? "text-orange" : ""}`}
                    style={value2Style}>
                    {value2 || "-"}
                </div>
            </div>
        </div>
    );
}
// #endregion
